import { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Copy, ShieldCheck, ShieldAlert } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';
import { useInboxSettings, useWhatsappWebMutations, whatsappError } from '../whatsappWebApi';

/**
 * Team Inbox settings: Test / Live mode, the test numbers, and the secured
 * webhook address to paste into Interakt. Admins can look; a Super Admin changes.
 */
export function InboxSettingsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { data, isLoading } = useInboxSettings(open);
  const { saveSettings } = useWhatsappWebMutations();
  const [numbers, setNumbers] = useState('');
  const [agents, setAgents] = useState<string[]>([]);
  const [confirmLive, setConfirmLive] = useState(false);
  const canChange = Boolean(data?.canChange);

  useEffect(() => {
    if (data) {
      setNumbers(data.testNumbers.join(', '));
      setAgents(data.agents);
    }
  }, [data]);

  const save = (payload: Parameters<typeof saveSettings.mutate>[0], done: string) =>
    saveSettings.mutate(payload, {
      onSuccess: () => { toast.success(done); setConfirmLive(false); },
      onError: (err) => toast.error(whatsappError(err, 'Could not save.')),
    });

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied.'); } catch { toast.error('Copy failed — select and copy it instead.'); }
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" title="WhatsApp inbox settings">
      {isLoading || !data ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : (
        <div className="flex flex-col gap-6">
          <section className="flex flex-col gap-3">
            <h3 className="text-title-sm font-semibold text-on-surface">Sending mode</h3>
            <div className={data.mode === 'live' ? 'rounded-lg border border-success/30 bg-success/10 p-3' : 'rounded-lg border border-warning/40 bg-warning/10 p-3'}>
              <p className="font-semibold text-on-surface">{data.mode === 'live' ? 'Live — the inbox can message any client' : 'Test — the inbox can message only the test numbers'}</p>
              <p className="mt-1 text-body-sm text-on-surface-variant">
                {data.mode === 'live'
                  ? 'Staff can reply to and message clients from the inbox.'
                  : 'Nothing can reach a client from the inbox by mistake while this is on.'}
              </p>
            </div>
            {canChange && (data.mode === 'test' ? (
              confirmLive ? (
                <div className="flex flex-col gap-2 rounded-lg border border-danger/30 p-3">
                  <p className="text-body-sm text-on-surface">Switch to Live? From now on, messages typed in the inbox go to real clients.</p>
                  <div className="flex gap-2">
                    <Button onClick={() => save({ mode: 'live' }, 'The inbox is live.')} disabled={saveSettings.isPending}>Yes, go live</Button>
                    <Button variant="outline" onClick={() => setConfirmLive(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button variant="outline" className="self-start" onClick={() => setConfirmLive(true)}>Switch to Live…</Button>
              )
            ) : (
              <Button variant="outline" className="self-start" onClick={() => save({ mode: 'test' }, 'Back in Test mode.')} disabled={saveSettings.isPending}>Switch back to Test</Button>
            ))}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-title-sm font-semibold text-on-surface">Test numbers</h3>
            <p className="text-body-sm text-on-surface-variant">In Test mode the inbox can message only these (10-digit, comma-separated).</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input
                value={numbers}
                onChange={(e) => setNumbers(e.target.value)}
                disabled={!canChange}
                className="min-w-0 flex-1 rounded-lg border border-outline px-3 py-2 text-body-md outline-none focus:border-primary disabled:bg-surface-variant"
              />
              {canChange && (
                <Button
                  variant="outline"
                  disabled={saveSettings.isPending}
                  onClick={() => save({ testNumbers: numbers.split(/[,\s]+/).filter(Boolean) }, 'Test numbers saved.')}
                >
                  Save numbers
                </Button>
              )}
            </div>
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="text-title-sm font-semibold text-on-surface">Inbox agents</h3>
            <p className="text-body-sm text-on-surface-variant">
              Admin and Super Admin see every conversation. These team members can use the inbox too, each seeing only their own clients' chats —
              keep it the same as the agents connected in Interakt.
            </p>
            <div className="max-h-56 overflow-y-auto rounded-lg border border-outline">
              {data.staff.map((u) => (
                <label key={u.username} className="flex cursor-pointer items-center gap-3 border-b border-outline/60 px-3 py-2 last:border-0 hover:bg-surface-variant/40">
                  <input
                    type="checkbox"
                    disabled={!canChange}
                    checked={agents.includes(u.username.toLowerCase())}
                    onChange={(e) => setAgents(e.target.checked ? [...agents, u.username.toLowerCase()] : agents.filter((a) => a !== u.username.toLowerCase()))}
                  />
                  <span className="min-w-0 flex-1 truncate text-body-md text-on-surface">{u.name}</span>
                  <span className="shrink-0 text-[12px] text-on-surface-variant">{u.username} · {u.role.replace(/_/g, ' ')}</span>
                </label>
              ))}
            </div>
            {canChange && (
              <Button variant="outline" className="self-start" disabled={saveSettings.isPending} onClick={() => save({ agents }, 'Inbox agents saved.')}>
                Save agents ({agents.length})
              </Button>
            )}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="flex items-center gap-2 text-title-sm font-semibold text-on-surface">
              {data.webhookSecured ? <ShieldCheck className="h-4 w-4 text-success-text" /> : <ShieldAlert className="h-4 w-4 text-warning-text" />}
              Incoming messages (Interakt webhook)
            </h3>
            <p className="text-body-sm text-on-surface-variant">
              Paste this address in Interakt → Settings → Developer Settings → Webhook URL, so customer replies and delivery ticks reach ZYNX.
              {data.webhookSecured ? ' It carries a secret, so nobody else can post messages into the inbox.' : ' It has no secret yet — anyone who finds it could post fake messages. Secure it, then paste the new address in Interakt.'}
            </p>
            <div className="flex items-center gap-2 rounded-lg border border-outline bg-surface-variant/40 px-3 py-2">
              <code className="min-w-0 flex-1 break-all text-[12px]">{data.webhookUrl}</code>
              <button type="button" onClick={() => copy(data.webhookUrl)} className="shrink-0 rounded p-1 hover:bg-black/5" title="Copy"><Copy className="h-4 w-4" /></button>
            </div>
            {canChange && (
              <Button
                variant="outline"
                className="self-start"
                disabled={saveSettings.isPending}
                onClick={() => save({ newWebhookToken: true }, 'New secret made — paste the new address in Interakt now.')}
              >
                {data.webhookSecured ? 'Make a new secret' : 'Secure the webhook'}
              </Button>
            )}
          </section>

          {!canChange && <p className="text-body-sm text-on-surface-variant">Only a Super Admin can change these settings.</p>}
        </div>
      )}
    </Modal>
  );
}
